/**
 * NetworkOpportunities — top-of-page hero showing three quick-action
 * opportunity cards on the /network page: Start a Study Session, browse
 * upcoming events, and (if needed) complete their profile. Each card is
 * self-contained and links out.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Video, Calendar, UserCog, Sparkles, ArrowRight, Users } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const NetworkOpportunities = () => {
  const { API, user } = useContext(AuthContext);
  const [nextEvent, setNextEvent] = useState(null);
  const [profileStrength, setProfileStrength] = useState(null);

  useEffect(() => {
    let alive = true;
    // Fetch next upcoming event
    axios.get(`${API}/public/events?limit=3`)
      .then((r) => {
        if (!alive) return;
        const items = Array.isArray(r.data) ? r.data : (r.data?.events || []);
        setNextEvent(items[0] || null);
      })
      .catch(() => {});
    // Fetch profile strength for the current user (calc client-side from /me profile)
    if (user?.id) {
      axios.get(`${API}/network/profile/${user.id}`)
        .then((r) => {
          if (!alive) return;
          const p = r.data || {};
          const filled = [
            !!p.avatar_url,
            !!p.headline,
            !!p.bio,
            (p.skills?.length || 0) >= 3,
            (p.work_history?.length || 0) >= 1 || (p.equipment?.length || 0) >= 1,
            !!p.website,
            !!p.location,
            !!p.handle,
          ].filter(Boolean).length;
          setProfileStrength(Math.round((filled / 8) * 100));
        })
        .catch(() => {});
    }
    return () => { alive = false; };
  }, [API, user?.id]);

  return (
    <section className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="network-opportunities">
      {/* Study Session CTA */}
      <Link
        to="/study-rooms"
        className="pro-card group p-4 relative overflow-hidden border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 hover:from-primary/15 hover:to-primary/10 transition-all"
        data-testid="opportunity-study-session"
      >
        <div className="relative z-10">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold mb-2">
            <Sparkles className="w-3 h-3" /> Opportunity
          </div>
          <h3 className="font-heading text-base font-extrabold text-foreground tracking-tight mb-1">
            Start a Study Session
          </h3>
          <p className="text-xs text-muted-foreground leading-relaxed mb-2">
            Spin up a Jitsi breakout room, invite your connections, and troubleshoot batches live.
          </p>
          <div className="inline-flex items-center gap-1 text-xs text-primary font-bold">
            <Video className="w-3.5 h-3.5" /> Open Study Rooms
            <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
        <Video className="absolute -bottom-3 -right-3 w-24 h-24 text-primary/10" strokeWidth={1.5} />
      </Link>

      {/* Next event */}
      <Link
        to="/community?tab=events"
        className="pro-card group p-4 relative overflow-hidden border border-secondary/30 bg-gradient-to-br from-secondary/10 to-secondary/5 hover:from-secondary/15 hover:to-secondary/10 transition-all"
        data-testid="opportunity-next-event"
      >
        <div className="relative z-10">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-secondary/15 text-secondary px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold mb-2">
            <Calendar className="w-3 h-3" /> Upcoming
          </div>
          {nextEvent ? (
            <>
              <h3 className="font-heading text-base font-extrabold text-foreground tracking-tight mb-1 line-clamp-1">
                {nextEvent.title}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed mb-2 line-clamp-2">
                {nextEvent.starts_at
                  ? (() => { try { return format(parseISO(nextEvent.starts_at), 'MMM d · h:mm a'); } catch { return ''; } })()
                  : 'Coming soon'}
                {nextEvent.subtitle ? ` — ${nextEvent.subtitle}` : ''}
              </p>
            </>
          ) : (
            <>
              <h3 className="font-heading text-base font-extrabold text-foreground tracking-tight mb-1">
                Community Events
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed mb-2">
                Browse workshops, cookalongs and instructor Q&amp;As. New events drop weekly.
              </p>
            </>
          )}
          <div className="inline-flex items-center gap-1 text-xs text-secondary font-bold">
            View events <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
        <Calendar className="absolute -bottom-3 -right-3 w-24 h-24 text-secondary/10" strokeWidth={1.5} />
      </Link>

      {/* Profile CTA or Directory */}
      {profileStrength != null && profileStrength < 90 ? (
        <Link
          to={`/u/${user?.handle || user?.id || ''}`}
          className="pro-card group p-4 relative overflow-hidden border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 hover:from-amber-500/15 hover:to-amber-500/10 transition-all"
          data-testid="opportunity-profile-strength"
        >
          <div className="relative z-10">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 text-amber-700 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold mb-2">
              <UserCog className="w-3 h-3" /> Boost profile
            </div>
            <h3 className="font-heading text-base font-extrabold text-foreground tracking-tight mb-1">
              You&apos;re {profileStrength}% complete
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-2">
              Add a photo, headline, and your equipment to appear in more suggestions.
            </p>
            <div className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-500 font-bold">
              Finish your profile <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
            </div>
          </div>
          <UserCog className="absolute -bottom-3 -right-3 w-24 h-24 text-amber-500/10" strokeWidth={1.5} />
        </Link>
      ) : (
        <Link
          to="/directory"
          className="pro-card group p-4 relative overflow-hidden border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 hover:from-emerald-500/15 hover:to-emerald-500/10 transition-all"
          data-testid="opportunity-directory"
        >
          <div className="relative z-10">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-700 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold mb-2">
              <Users className="w-3 h-3" /> Discover
            </div>
            <h3 className="font-heading text-base font-extrabold text-foreground tracking-tight mb-1">
              Browse the Directory
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-2">
              Find operators by machine brand, region or expertise. Filter, connect, and DM.
            </p>
            <div className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-500 font-bold">
              Open Directory <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
            </div>
          </div>
          <Users className="absolute -bottom-3 -right-3 w-24 h-24 text-emerald-500/10" strokeWidth={1.5} />
        </Link>
      )}
    </section>
  );
};

export default NetworkOpportunities;
